import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CommunityChoice } from "@/components/app/fields/community-choice";
import { PageHeader } from "@/components/app/shell/page-header";
import { SettingsScopeLine } from "@/components/app/rails/settings-scope-line";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { SHELBY_SECTIONS, type ShelbySectionId } from "./-lib/settings-sections";
import { ShelbySettingsForm } from "./-components/shelby-settings-form";
import { NeedsActivation } from "@/components/app/activation/needs-activation";

export const Route = createFileRoute("/_authenticated/_shelby/shelby/settings")({
  validateSearch: (search: Record<string, unknown>): { section?: ShelbySectionId } =>
    SHELBY_SECTIONS.find((id) => id === search.section && id !== "answering")
      ? { section: search.section as ShelbySectionId }
      : {},
  beforeLoad: ({ context }) => guardRoute(context.me, "shelby-settings"),
  component: ShelbySettings,
});

/** One community's sales assistant. The warnings at the top are the point of the screen: she can be
 * switched on and still be silent — no texting line, the front desk not answering texts, nobody
 * named to alert — and every one of those is invisible from the settings alone. */
function ShelbySettings() {
  const { facilityId } = useFacility();
  const { navigation } = usePolicy();
  const oneBuilding = facilityId === "all" ? null : facilityId;
  const canTry = navigation.some((entry) => entry.id === "shelby-agent");

  const settings = useQuery({
    ...orpc.shelby.settings.queryOptions({ input: { facilityId: oneBuilding ?? "" } }),
    enabled: Boolean(oneBuilding),
  });
  const contacts = useQuery({
    ...orpc.shelby.contacts.queryOptions({ input: { facilityId: oneBuilding ?? "" } }),
    enabled: Boolean(oneBuilding),
  });

  if (!oneBuilding) {
    return (
      <>
        <SettingsScopeLine />
        <PageHeader
          title="Sales assistant"
          meta="Who answers a family asking about moving in, and who she puts on the conversation."
        />
        <Card>
          <CardContent>
            <CommunityChoice prompt="Choose a building. She is switched on per community, answers on its texting line and alerts its admissions person." />
          </CardContent>
        </Card>
      </>
    );
  }

  const readiness = settings.data?.readiness;
  const name = settings.data?.settings.agentName ?? "The sales assistant";

  return (
    <>
      <SettingsScopeLine />
      <PageHeader
        title="Sales assistant"
        meta={
          settings.data
            ? `${settings.data.name}. ${name} answers families asking about moving in, and her whole job is getting a person onto the conversation.`
            : undefined
        }
        actions={
          canTry ? (
            <Button variant="outline" asChild>
              <Link to="/shelby/agent">Try her</Link>
            </Button>
          ) : null
        }
      />

      {settings.isPending || contacts.isPending || !settings.data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {readiness && settings.data.settings.enabled ? (
            <>
              {readiness.textingLine ? null : (
                <NeedsActivation
                  featureKey="texting"
                  facilityId={oneBuilding}
                  title="This community has no texting line"
                >
                  Nothing can reach her: a text needs a number that receives SMS. The People AI team
                  provisions one for the community and connects it.
                </NeedsActivation>
              )}
              {readiness.textingLineIsVoiceLine ? (
                <Alert variant="destructive">
                  <AlertTitle>The texting line is the published number</AlertTitle>
                  <AlertDescription>
                    A published number that forwards calls does not forward texts: the carrier drops
                    every message and nobody is told. Text from the number provisioned here instead.
                  </AlertDescription>
                </Alert>
              ) : null}
              {readiness.frontDeskAnswersTexts ? null : (
                <Alert>
                  <AlertTitle>The front desk is not answering texts</AlertTitle>
                  <AlertDescription>
                    She rides on the same switch. Turn "Sarah answers texts" on under Front Desk
                    Settings, or she will never see one.
                  </AlertDescription>
                </Alert>
              )}
              {readiness.salesContact ? null : (
                <Alert>
                  <AlertTitle>
                    {readiness.salesContactConfigured
                      ? "The admissions contact cannot take a text"
                      : "No admissions contact"}
                  </AlertTitle>
                  <AlertDescription>
                    {readiness.salesContactConfigured
                      ? "The person named below cannot be reached by text and nobody covers for them, so no phone buzzes when she takes a conversation."
                      : "She will answer and take a complete message, but nobody is alerted. Name somebody below."}
                  </AlertDescription>
                </Alert>
              )}
              {readiness.salesContact ? (
                <p className="text-muted-foreground text-sm">
                  {readiness.salesContact.name}
                  {readiness.salesContact.section ? `, ${readiness.salesContact.section}` : ""} is
                  texted at {readiness.salesContact.phone} the moment she takes a conversation
                  {readiness.salesContact.via.length > 0
                    ? ` (covering for ${readiness.salesContact.via.join(", ")})`
                    : ""}
                  .
                </p>
              ) : null}
            </>
          ) : null}

          <ShelbySettingsForm
            key={oneBuilding}
            facilityId={oneBuilding}
            settings={settings.data}
            contacts={contacts.data ?? []}
          />
        </>
      )}
    </>
  );
}
